import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";

import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";

import FeatherIcon from "feather-icons-react";

import Breadcrumb from "../../../../layouts-v2/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../../components-v2/container/PageContainer";
import ReleaseDetailsChecklist from "./release-details-check-list/ReleaseDetailsChecklist";

import { RevisionService } from "../../../../services-v2/eitri-manager-api/RevisionService";
import { EnvironmentService } from "../../../../services-v2/eitri-manager-api/EnvironmentService";
import { ShareService } from "../../../../services-v2/share-api/ShareService";
import QrCode from "qrcode.react";
import useAlert from "../../../../hooks/useAlert";
import useStatus from "../../../../hooks/useStatus";
import { useParams } from "react-router-dom";

import Loading from "../../../../layouts-v2/loading/Loading";
import CustomButton from "../../../../components-v2/custom-buttom/CustomButton";

const PAGE_TITLE = "Versões";
const EITRI_HOST = process.env.REACT_APP_BACKEND_HOST;

const ListRevision = () => {
    const [revisions, setRevisions] = React.useState([{ environments: [] }]);
    const [
        applicationEnvironments,
        setApplicationEnvironments
    ] = React.useState([]);
    const [finalRevisions, setFinalRevisions] = React.useState([]);
    const [currentRevision, setCurrentRevision] = React.useState({});
    const [releaseDetail, setReleaseDetail] = React.useState({
        slug: "",
        version: "",
        environment: ""
    });
    const [
        isPublishConfirmationDialogVisible,
        setIsPublishConfirmationDialogVisible
    ] = React.useState(false);
    const [isQrCodeDialogVisible, setIsQrCodeDialogVisible] = React.useState(
        false
    );
    const [versionDeeplink, setVersionDeeplink] = React.useState("");
    const { alertInfo, setAllAlertInfo, setInitialAlertInfo } = useAlert();
    const { status, setAllStatus } = useStatus();
    const params = useParams();

    React.useEffect(() => {
        const eitriAppId = params.eitriAppId;
        loadVersions(eitriAppId);
    }, []);

    async function loadVersions(eitriAppId) {
        try {
            setAllStatus({ isFetching: true, success: false, error: false });
            const revisions = await fetchRevisions(eitriAppId);
            if (revisions.length === 0) {
                setAllStatus({
                    isFetching: false,
                    success: true,
                    error: false
                });
                setAllAlertInfo({
                    theme: "info",
                    message:
                        "Nenhuma versão. 🌟  Sua chance de fazer história!",
                    visible: true
                });
                return;
            }
            const applicationId = revisions[0].applicationId;
            await fetchApplicationEnvironments(applicationId);
            setAllStatus({ isFetching: false, success: true, error: false });
        } catch (e) {
            console.error("Error!");
            setAllAlertInfo({
                theme: "error",
                message: e.message,
                visible: true
            });
            setAllStatus({ isFetching: false, success: false, error: true });
        }
    }

    async function fetchRevisions(eitriAppId) {
        try {
            const response = await RevisionService.getRevisions(eitriAppId);
            setRevisions(response);
            return response;
        } catch (err) {
            console.error(err);
            throw Error(
                "Ops! Não foi possível carregar as versões do eitri-app."
            );
        }
    }

    async function fetchApplicationEnvironments(applicationId) {
        try {
            const response = await EnvironmentService.getEnvironments(
                applicationId
            );
            setApplicationEnvironments(response);
        } catch (e) {
            console.error(err);
            throw Error("Ops! Não foi possível carregar os ambientes.");
        }
    }

    React.useEffect(() => {
        const finalRevisions = revisions.map(revision => {
            return {
                id: revision?.id ?? "",
                slug: revision?.slug,
                version: revision.version,
                environments: mergeApplicationEnvironmentsWithRevisions(
                    applicationEnvironments,
                    revision
                ),
                updateAt: revision.updateAt,
                url: revision.url,
                eitriAppId: revision.eitriAppId,
                eitriAppSubset: revision.eitriAppSubset,
                createdBy: revision.createdBy
            };
        });
        setFinalRevisions(finalRevisions);
    }, [applicationEnvironments, revisions]);

    const mergeApplicationEnvironmentsWithRevisions = (
        applicationEnvironments,
        revision
    ) => {
        const revisionEnvironments = revision.environments
            ? revision.environments
            : [];

        return applicationEnvironments.map(applicationEnv => {
            const matchingRevisionEnv = revisionEnvironments.find(
                revisionEnv => revisionEnv.name === applicationEnv.name
            );

            return matchingRevisionEnv || applicationEnv;
        });
    };

    const openPublishConfirmationDialog = (revision, environment) => {
        if (environment.status === "PUBLISHED") {
            setAllAlertInfo({
                theme: "info",
                message: `A versão ${currentRevision.version} do ${currentRevision.slug} já está publicada em ${environment.name}.`,
                visible: true
            });
            return;
        }
        const releaseDetail = {
            slug: revision.slug,
            version: revision.version,
            environment: environment.name
        };
        setReleaseDetail(releaseDetail);
        setCurrentRevision(revision);
        setIsPublishConfirmationDialogVisible(true);
    };

    async function openQrCodeDialog(revision) {
        try {
            const sharableState = {
                id: revision.eitriAppId,
                state: {
                    developerEmail: revision.createdBy,
                    slug: revision.slug,
                    title: revision.eitriAppSubset.title,
                    miniAppBootstrapUrl: revision.url,
                    view: "index.html"
                }
            };
            const resp = await ShareService.createSharableState(sharableState);
            const sharableStateID = resp.id;
            const deeplinkFullUrl = `${EITRI_HOST}/workspace/share/${sharableStateID}`;
            setVersionDeeplink(deeplinkFullUrl);
            setIsQrCodeDialogVisible(true);
        } catch (e) {
            throw Error("Não foi possível gerar o qrCode.", e);
        }
    }

    const publishingVersion = async () => {
        setIsPublishConfirmationDialogVisible(false);
        try {
            const envToPublish = currentRevision.environments.filter(
                env => env.name === releaseDetail.environment
            )[0];
            const revisionId = currentRevision.id;
            const environmentId = envToPublish.id;
            const eitriAppId = currentRevision.eitriAppId;
            await RevisionService.publishingRevision(revisionId, environmentId);
            loadVersions(eitriAppId);
            setAllAlertInfo({
                theme: "success",
                message: `A versão ${currentRevision.version} do eitri app ${currentRevision.slug} foi publicada em ${envToPublish.name}.`,
                visible: true
            });
        } catch (error) {
            setAllAlertInfo({
                theme: "error",
                message: `Ops! Não foi possível publicar a revisão ${currentRevision.version} do eitri app ${currentRevision.slug}.`,
                visible: true
            });
            console.error(error);
        } finally {
            setIsPublishConfirmationDialogVisible(false);
        }
    };

    const BCrumb = [
        {
            to: "/eitri-app-management/eitri-apps",
            title: "Eitri-Apps"
        },
        {
            title: `${PAGE_TITLE} ${
                finalRevisions[0]?.slug ? finalRevisions[0]?.slug : ""
            }`
        }
    ];

    return (
        <>
            {status.isFetching ? (
                <Loading isVisible={status.isFetching} />
            ) : (
                <PageContainer
                    title={PAGE_TITLE}
                    description={`this is ${PAGE_TITLE} page`}
                >
                    <Collapse in={alertInfo.visible}>
                        <Alert
                            variant="filled"
                            severity={alertInfo.theme || "info"}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setInitialAlertInfo();
                                    }}
                                >
                                    <FeatherIcon icon="x" width="20" />
                                </IconButton>
                            }
                        >
                            {alertInfo.message}
                        </Alert>
                    </Collapse>
                    {/* breadcrumb */}
                    <Breadcrumb items={BCrumb} />
                    {/* end breadcrumb */}
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    overflow: {
                                        xs: "auto",
                                        sm: "unset"
                                    }
                                }}
                            >
                                <Table
                                    aria-label="simple table"
                                    sx={{
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="h5">
                                                    Versões
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="h5">
                                                    Ambientes
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {finalRevisions.map(revision => (
                                            <TableRow key={revision.id}>
                                                <TableCell>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        <Box
                                                            sx={{
                                                                cursor:
                                                                    "pointer"
                                                            }}
                                                            onClick={() =>
                                                                openQrCodeDialog(
                                                                    revision
                                                                )
                                                            }
                                                        >
                                                            <QrCodeScannerIcon
                                                                className={`cypress-qr-${revision.id}`}
                                                                fontSize={
                                                                    "large"
                                                                }
                                                                sx={{
                                                                    cursor:
                                                                        "pointer"
                                                                }}
                                                            />
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                ml: 2
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="h6"
                                                                fontWeight="600"
                                                            >
                                                                Versão
                                                            </Typography>
                                                            <Typography
                                                                color="textSecondary"
                                                                variant="h6"
                                                                fontWeight="400"
                                                            >
                                                                {
                                                                    revision.version
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack
                                                        spacing={1}
                                                        direction={{
                                                            xs: "column",
                                                            sm: "row"
                                                        }}
                                                    >
                                                        {revision.environments.map(
                                                            (
                                                                environment,
                                                                id
                                                            ) => {
                                                                return (
                                                                    <Chip
                                                                        className={`cypress-publish-${environment.name}-${id}`}
                                                                        key={id}
                                                                        size="small"
                                                                        label={environment.name.toLowerCase()}
                                                                        color="primary"
                                                                        variant={
                                                                            environment.status ===
                                                                            "PUBLISHED"
                                                                                ? ""
                                                                                : "outlined"
                                                                        }
                                                                        clickable
                                                                        onClick={() =>
                                                                            openPublishConfirmationDialog(
                                                                                revision,
                                                                                environment
                                                                            )
                                                                        }
                                                                    />
                                                                );
                                                            }
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </CardContent>
                    </Card>

                    <Dialog
                        open={isPublishConfirmationDialogVisible}
                        onClose={() =>
                            setIsPublishConfirmationDialogVisible(false)
                        }
                        fullWidth
                    >
                        {/* <form onSubmit={() => console.log("submitHandler")}> */}
                        <DialogContent>
                            <Typography variant="h3" sx={{ mb: 2 }}>
                                Você está pronto para publicar no Eitri?
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 2 }}>
                                Antes de prosseguir, por favor, confirme as
                                seguintes informações:
                            </Typography>

                            <ReleaseDetailsChecklist
                                releaseDetail={releaseDetail}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() =>
                                    setIsPublishConfirmationDialogVisible(false)
                                }
                            >
                                Cancel
                            </Button>
                            <CustomButton
                                variant="contained"
                                onClick={publishingVersion}
                            >
                                Publicar
                            </CustomButton>
                        </DialogActions>
                        {/* </form> */}
                    </Dialog>

                    <Dialog
                        open={isQrCodeDialogVisible}
                        onClose={() => setIsQrCodeDialogVisible(false)}
                        fullWidth
                    >
                        <DialogContent>
                            <Typography variant="h3" sx={{ mb: 2 }}>
                                Acesso Móvel Simplificado: Escaneie o QR Code
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 2 }}>
                                Sinta a Experiência do Eitri App em seu
                                Smartphone.
                            </Typography>
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                            >
                                <QrCode
                                    id="qrCode"
                                    value={versionDeeplink}
                                    size={200}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                className="cypress-bt-qr-code"
                                variant="contained"
                                onClick={() => setIsQrCodeDialogVisible(false)}
                            >
                                Fechar
                            </Button>
                        </DialogActions>
                    </Dialog>
                </PageContainer>
            )}
        </>
    );
};

export default ListRevision;
